import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { T, REL } from "../../theme.js";
import { usePeople } from "../../context/PeopleContext.jsx";
import { Tag } from "../ui/index.jsx";
import { fmtAnniversary, fmtBirthday } from "../../utils/helpers.js";
import { AddPersonSheet } from "./AddPersonSheet.jsx";
import { PersonDrawer, RemovePersonDialog } from "./PersonDrawer.jsx";
import { FairnessContent, AnchorsContent } from "../schedule/ScheduleContent.jsx";
import { Capacitor } from "@capacitor/core";
import { supabase } from "../../supabaseClient.js";
import apiFetch from "../../apiFetch.js";

const DISMISSED_KEY = "orbit_contact_suggestions_dismissed";

// ── Local helpers ─────────────────────────────────────────────────────────────
const _now = new Date();
const _MONTH_DAYS = new Date(_now.getFullYear(), _now.getMonth() + 1, 0).getDate();
const _TODAY_DAY = _now.getDate();

function monthlyTgt(p) { return p.tgtMode === "week" ? Math.round(p.tgt * 4.33) : p.tgt; }

// ── Constellation chart — all people orbiting "Me" ────────────────────────────
function idSeed(id) {
  if (typeof id === "number") return id;
  if (typeof id === "string") {
    let h = 0;
    for (let i = 0; i < id.length; i++) { h = (h * 31 + id.charCodeAt(i)) | 0; }
    return Math.abs(h) || 1;
  }
  return 1;
}

function PeopleOrbitalChart({ people, selectedId, onSelect, onSelectMe, filterType }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [hoveredId, setHoveredId] = useState(null);
  const imgCache = useRef({});
  const { user } = usePeople();
  const userPhoto = user?.user_metadata?.photo || null;

  useEffect(() => {
    people.forEach(p => {
      if (p.photo && !imgCache.current[p.id]) {
        const img = new Image();
        img.src = p.photo;
        imgCache.current[p.id] = img;
      }
    });
  }, [people]);

  useEffect(() => {
    if (userPhoto && !imgCache.current["__me__"]) {
      const img = new Image();
      img.src = userPhoto;
      img.onload = () => { imgCache.current["__me__"] = img; };
      imgCache.current["__me__"] = img;
    }
  }, [userPhoto]);

  const positioned = useMemo(() => {
    const typeOrder = ["partner", "family", "friend", "coparent"];
    const typeAngles = { partner: -Math.PI / 2, family: Math.PI / 6, friend: Math.PI * 5 / 6, coparent: -Math.PI * 2 / 3 };
    const typeSpread = { partner: 0.55, family: 0.45, friend: 0.5, coparent: 0.3 };
    const typeRadius = { partner: 80, family: 115, friend: 100, coparent: 130 };

    const byType = {};
    typeOrder.forEach(t => { byType[t] = people.filter(p => p.type === t); });

    return people.map(p => {
      const peers = byType[p.type];
      const idx = peers.indexOf(p);
      const n = peers.length;
      const baseAngle = typeAngles[p.type] ?? 0;
      const spread = typeSpread[p.type] ?? 0.5;
      const offset = n > 1 ? (idx / (n - 1) - 0.5) * spread * 2 : 0;
      const angle = baseAngle + offset;
      const orbitR = typeRadius[p.type] ?? 100;
      const r = orbitR + (idx % 2 === 0 ? 0 : 18);
      return { ...p, _angle: angle, _r: r };
    });
  }, [people]);

  const draw = useCallback((t) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height, cx = W / 2, cy = H / 2;
    ctx.clearRect(0, 0, W, H);

    const bg = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx * 0.7);
    bg.addColorStop(0, "rgba(193,123,78,0.06)"); bg.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    positioned.forEach(p => {
      const seed = idSeed(p.id);
      const fade = filterType !== "all" && p.type !== filterType;
      const angle = p._angle + Math.sin(t * 0.00015 + seed * 0.7) * 0.04;
      const px = cx + Math.cos(angle) * p._r;
      const py = cy + Math.sin(angle) * p._r;
      const c = REL[p.type]?.color ?? "#999";
      const alpha = fade ? 0.05 : 0.22;
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(px, py);
      ctx.strokeStyle = c + Math.round(alpha * 255).toString(16).padStart(2, "0");
      ctx.lineWidth = fade ? 0.5 : 1.2; ctx.setLineDash([]); ctx.stroke();
      p._px = px; p._py = py;
    });

    const meR = 22;
    if (hoveredId === "me") {
      ctx.beginPath(); ctx.arc(cx, cy, meR + 8, 0, Math.PI * 2);
      ctx.strokeStyle = T.clay + "55"; ctx.lineWidth = 1.2; ctx.stroke();
    }
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, meR, 0, Math.PI * 2);
    const meImg = imgCache.current["__me__"];
    if (meImg && meImg.complete && meImg.naturalWidth > 0) {
      ctx.clip();
      ctx.drawImage(meImg, cx - meR, cy - meR, meR * 2, meR * 2);
      ctx.restore();
      ctx.beginPath(); ctx.arc(cx, cy, meR, 0, Math.PI * 2);
      ctx.strokeStyle = T.clay + "AA"; ctx.lineWidth = 1.5; ctx.stroke();
    } else {
      const meG = ctx.createRadialGradient(cx - 3, cy - 3, 2, cx, cy, meR);
      meG.addColorStop(0, "#E8D5BC"); meG.addColorStop(1, T.clay);
      ctx.fillStyle = meG; ctx.fill();
      ctx.restore();
      ctx.font = "bold 9px 'DM Sans',sans-serif";
      ctx.fillStyle = T.bark; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("ME", cx, cy);
    }

    positioned.forEach(p => {
      const fade = filterType !== "all" && p.type !== filterType;
      const c = REL[p.type]?.color ?? "#999";
      const px = p._px, py = p._py;
      const isSelected = selectedId === p.id;
      const isHovered = hoveredId === p.id && !isSelected;
      const nodeR = 13;

      if (isHovered) {
        ctx.beginPath(); ctx.arc(px, py, nodeR + 6, 0, Math.PI * 2);
        ctx.strokeStyle = c + "55"; ctx.lineWidth = 1.2; ctx.stroke();
      }

      if (isSelected) {
        const gg = ctx.createRadialGradient(px, py, 0, px, py, nodeR + 10);
        gg.addColorStop(0, c + "55"); gg.addColorStop(1, "transparent");
        ctx.beginPath(); ctx.arc(px, py, nodeR + 10, 0, Math.PI * 2);
        ctx.fillStyle = gg; ctx.fill();
        ctx.beginPath(); ctx.arc(px, py, nodeR + 3, 0, Math.PI * 2);
        ctx.strokeStyle = c + "CC"; ctx.lineWidth = 1.8; ctx.stroke();
      }

      const cachedImg = imgCache.current[p.id];
      if (cachedImg && cachedImg.complete && cachedImg.naturalWidth > 0 && !fade) {
        ctx.save();
        ctx.beginPath(); ctx.arc(px, py, nodeR, 0, Math.PI * 2); ctx.clip();
        ctx.drawImage(cachedImg, px - nodeR, py - nodeR, nodeR * 2, nodeR * 2);
        ctx.restore();
        ctx.beginPath(); ctx.arc(px, py, nodeR, 0, Math.PI * 2);
        ctx.strokeStyle = c + "88"; ctx.lineWidth = 1.5; ctx.stroke();
      } else {
        ctx.beginPath(); ctx.arc(px, py, nodeR, 0, Math.PI * 2);
        const ng = ctx.createRadialGradient(px - 2, py - 2, 1, px, py, nodeR);
        ng.addColorStop(0, c + (fade ? "44" : "CC")); ng.addColorStop(1, c + (fade ? "22" : "77"));
        ctx.fillStyle = ng; ctx.fill();
        ctx.font = `bold 7px 'DM Sans',sans-serif`;
        ctx.fillStyle = fade ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.9)";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(p.ini || p.name[0], px, py);
      }

      const offX = Math.cos(p._angle) * 22, offY = Math.sin(p._angle) * 20;
      ctx.font = `${isSelected ? "600" : "500"} 9.5px 'DM Sans',sans-serif`;
      ctx.fillStyle = fade ? T.muted : isSelected ? c : T.bark;
      ctx.textAlign = offX > 3 ? "left" : offX < -3 ? "right" : "center";
      ctx.fillText(p.name, px + offX, py + offY);
    });

    animRef.current = requestAnimationFrame(nt => draw(nt));
  }, [positioned, selectedId, filterType, hoveredId]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(t => draw(t));
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  function hitNode(e) {
    const canvas = canvasRef.current; if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width, sy = canvas.height / rect.height;
    const mx = (e.clientX - rect.left) * sx, my = (e.clientY - rect.top) * sy;
    const cx = canvas.width / 2, cy = canvas.height / 2;
    if (Math.sqrt((mx - cx) ** 2 + (my - cy) ** 2) < 26) return "me";
    for (const p of positioned) {
      if (p._px === undefined) continue;
      const dx = mx - p._px, dy = my - p._py;
      if (Math.sqrt(dx * dx + dy * dy) < 18) return p.id;
    }
    return null;
  }

  function handleClick(e) {
    const id = hitNode(e);
    if (id === "me") { onSelectMe?.(); return; }
    if (id != null) { onSelect(id === selectedId ? null : id); return; }
    onSelect(null);
  }

  function handleMouseMove(e) {
    setHoveredId(hitNode(e));
  }

  return (<canvas ref={canvasRef} width={320} height={320} onClick={handleClick} onMouseMove={handleMouseMove} onMouseLeave={() => setHoveredId(null)}
    style={{ width: "100%", maxWidth: 320, cursor: hoveredId ? "pointer" : "default", display: "block" }} />);
}

// ── Avatar with type ring + pip ───────────────────────────────────────────────
function DirAvatar({ p, size: _size = 46, selected, onPhotoClick }) {
  const size = Math.max(_size, 24);
  const cfg = REL[p.type]; const c = (!p.photo && p.avatarColor) || cfg.color;
  const ringStyles = { partner: { border: `2px solid ${c}88` }, family: { border: `2px dashed ${c}88` }, friend: { border: `2px dotted ${c}88` }, coparent: { border: `3px double ${c}` } };
  return (<div onClick={onPhotoClick} style={{ width: size, height: size, borderRadius: "50%", background: c + "20", ...(ringStyles[p.type] || {}), boxShadow: selected ? `0 0 0 3px ${c}35` : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * .28, fontWeight: 700, color: c, fontFamily: "'Crimson Pro',serif", flexShrink: 0, position: "relative", transition: "all .18s", overflow: "hidden", cursor: onPhotoClick ? "pointer" : "default" }}>
    {p.photo ? <img src={p.photo} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} /> : (p.ini || p.name[0])}
    {onPhotoClick && <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity .15s" }} className="photo-overlay">
      <span style={{ fontSize: size * .22, color: "white" }}>📷</span>
    </div>}
  </div>);
}

function fmtLastSeen(lastActive) {
  if (!lastActive) return null;
  const ms = Date.now() - new Date(lastActive).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

// ── Person grid card ──────────────────────────────────────────────────────────
function DirCard({ p, isSelected, onClick }) {
  const cfg = REL[p.type]; const c = cfg.color;
  const { pendingOutgoingIds, pendingSchedulingIds } = usePeople();
  const isPendingConnection = p.linkedUserId && pendingOutgoingIds?.has(p.linkedUserId);
  const isPendingScheduling = p.linkedUserId && pendingSchedulingIds?.has(p.linkedUserId);
  const isPending = isPendingConnection || isPendingScheduling;
  const anniv = p.anniversary ? fmtAnniversary(p.anniversary) : null;
  const bday = p.birthday ? fmtBirthday(p.birthday) : null;
  const upcomingEvent = bday?.upcoming || anniv?.upcoming;
  return (<div onClick={onClick} style={{ background: isSelected ? c + "0A" : T.surface, border: `1.5px solid ${isSelected ? c + "55" : upcomingEvent ? c + "30" : T.edge}`, borderRadius: 16, padding: "16px 18px", cursor: "pointer", transition: "all .18s", boxShadow: isSelected ? `0 4px 20px ${c}14` : "0 1px 4px rgba(0,0,0,0.04)", display: "flex", flexDirection: "column" }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12, flex: 1 }}>
      <DirAvatar p={p} size={46} selected={isSelected} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{p.name}</span>
          <span style={{ fontSize:11, padding: "1px 7px", borderRadius: 10, background: c + "18", border: `1px solid ${c}30`, color: c, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{cfg.label}</span>
          {bday?.upcoming && <span style={{ fontSize:11, color: T.amber, background: T.amber + "14", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>🎂 {bday.diffDays === 0 ? "today" : `${bday.diffDays}d`}</span>}
          {anniv?.upcoming && <span style={{ fontSize:11, color: c, background: c + "14", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>♡ {anniv.diffDays === 0 ? "today" : `${anniv.diffDays}d`}</span>}
          {isPending && <span style={{ fontSize:11, color: T.clay, background: T.clay + "18", border: `1px solid ${T.clay}35`, padding: "1px 6px", borderRadius: 8, fontWeight: 600, letterSpacing: "0.02em" }}>Awaiting their reply</span>}
        </div>
        {p.linkedUserId && <p style={{ fontSize: 11, color: T.muted, marginBottom: 2 }}>Last seen: {fmtLastSeen(p.lastActive) || "—"}</p>}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {p.tags.slice(0, 3).map(t => <Tag key={t} label={t} color={c} />)}
        </div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 8 }}>
      <div style={{ flex: 1, padding: "6px 8px", borderRadius: 8, background: T.mist, textAlign: "center" }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: c }}>{p.next || "TBD"}</p>
        <p style={{ fontSize:11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>next</p>
      </div>
{bday && <div style={{ flex: 1, padding: "6px 8px", borderRadius: 8, background: bday.upcoming ? T.amber + "12" : T.mist, textAlign: "center" }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: bday.upcoming ? T.amber : T.bark }}>{bday.label}</p>
        <p style={{ fontSize:11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>bday</p>
      </div>}
      {p.tgt && <div style={{ flex: 1, padding: "6px 8px", borderRadius: 8, background: T.mist, textAlign: "center" }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: Math.round(p.act / monthlyTgt(p) * 100) >= 90 ? T.sage : T.amber }}>{Math.round(p.act / monthlyTgt(p) * 100)}%</p>
        <p style={{ fontSize:11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>nights</p>
      </div>}
    </div>
  </div>);
}

// ── PeopleContent ─────────────────────────────────────────────────────────────
const PEOPLE_SUB_TABS=[
  {id:"directory",label:"People",icon:"👥"},
  {id:"fairness",label:"Fairness",icon:"⚖️"},
  {id:"anchors",label:"Anchors",icon:"⚓"},
];

export function PeopleContent({ onNav, isMobile, initialType }) {
  // Sub-tab: "fairness" or "anchors" show those views, anything else shows directory
  const subTabInit = (initialType === "fairness" || initialType === "anchors") ? initialType : "directory";
  const [subTab, setSubTab] = useState(subTabInit);

  // Sync sub-tab when initialType prop changes (e.g. from nav callback)
  useEffect(() => {
    if (initialType === "fairness" || initialType === "anchors") setSubTab(initialType);
    else setSubTab("directory");
  }, [initialType]);

  if (subTab === "fairness") {
    return (<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <PeopleSubTabBar subTab={subTab} setSubTab={setSubTab} isMobile={isMobile}/>
      <FairnessContent onNav={onNav} isMobile={isMobile}/>
    </div>);
  }
  if (subTab === "anchors") {
    return (<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <PeopleSubTabBar subTab={subTab} setSubTab={setSubTab} isMobile={isMobile}/>
      <AnchorsContent onNav={onNav} isMobile={isMobile}/>
    </div>);
  }

  return <PeopleDirectoryContent onNav={onNav} isMobile={isMobile} initialType={initialType} subTab={subTab} setSubTab={setSubTab}/>;
}

function PeopleSubTabBar({subTab, setSubTab, isMobile}) {
  return (
    <div style={{display:"flex",gap:4,padding:isMobile?"8px 18px 0":"10px 24px 0",background:T.panel,flexShrink:0}}>
      {PEOPLE_SUB_TABS.map(t=>(
        <button key={t.id} onClick={()=>setSubTab(t.id)} style={{
          padding:"6px 14px",borderRadius:20,border:`1.5px solid ${subTab===t.id?T.clay:T.edge}`,
          background:subTab===t.id?T.clay+"14":"transparent",color:subTab===t.id?T.clay:T.muted,
          fontSize:12,fontWeight:subTab===t.id?600:400,cursor:"pointer",display:"flex",alignItems:"center",gap:5,
          transition:"all .15s",fontFamily:"'DM Sans',sans-serif"
        }}>
          <span style={{fontSize:13}}>{t.icon}</span>{t.label}
        </button>
      ))}
    </div>
  );
}

function PeopleDirectoryContent({ onNav, isMobile, initialType, subTab, setSubTab }) {
  const { people: PEOPLE, removePerson, addPerson, updatePerson } = usePeople();
  const [tf, setTf] = useState((initialType && initialType !== "fairness" && initialType !== "anchors") ? initialType : "all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("lastSeen");
  const [fullProfileId, setFullProfileId] = useState(null);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonPrefill, setAddPersonPrefill] = useState(null);
  const [connReqSentPopup, setConnReqSentPopup] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const scrollRef = useRef(null);
  const firstLetterRefs = useRef({});

  // ── Contact-matching ("People you may know") ──────────────────────────────
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]")); }
    catch { return new Set(); }
  });

  const dismissSuggestion = (id) => {
    setDismissedIds(prev => {
      const next = new Set(prev); next.add(id);
      try { localStorage.setItem(DISMISSED_KEY, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Only run once per session
    let cancelled = false;
    (async () => {
      try {
        setSuggestionsLoading(true);
        // Request permission
        // Dynamic import — keeps @capacitor-community/contacts out of the web bundle
        const { Contacts } = await import("@capacitor-community/contacts");
        const perm = await Contacts.requestPermissions();
        if (perm.contacts === "denied") return;

        // Read all contacts (phones + emails only)
        const { contacts } = await Contacts.getContacts({
          projection: { name: false, phones: true, emails: true },
        });

        const phones = [];
        const emails = [];
        for (const c of (contacts || [])) {
          for (const ph of (c.phones || [])) {
            if (ph.number) phones.push(ph.number);
          }
          for (const em of (c.emails || [])) {
            if (em.address) emails.push(em.address);
          }
        }

        if (phones.length === 0 && emails.length === 0) return;

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token || cancelled) return;

        const res = await apiFetch("/api/contacts/match", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ phones, emails }),
        });
        if (!res.ok || cancelled) return;
        const json = await res.json();

        // Filter out people already in the user's orbit and dismissed ones
        const existingLinkedIds = new Set(PEOPLE.map(p => p.linkedUserId).filter(Boolean));
        const fresh = (json.matches || []).filter(m => !existingLinkedIds.has(m.id) && !dismissedIds.has(m.id));
        if (!cancelled) setContactSuggestions(fresh);
      } catch (err) {
        console.warn("[contact-match]", err?.message || err);
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = Object.fromEntries([["all", PEOPLE.length], ...Object.keys(REL).map(k => [k, PEOPLE.filter(p => p.type === k).length])]);
  const TYPE_ORDER = Object.keys(REL);

  const filtered = useMemo(() => {
    let list = tf === "all" ? PEOPLE : PEOPLE.filter(p => p.type === tf);
    if (search.trim()) { const q = search.toLowerCase(); list = list.filter(p => p.name.toLowerCase().includes(q) || p.tags.some(t => t.includes(q))); }
    return [...list].sort((a, b) => {
      if (tf === "all") { const td = TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type); if (td !== 0) return td; }
      return sortBy === "name" ? a.name.localeCompare(b.name) : a.seen - b.seen;
    });
  }, [PEOPLE, tf, search, sortBy]);

  const letterMap = useMemo(() => {
    const m = {};
    filtered.forEach((p, idx) => { const letter = p.name[0].toUpperCase(); if (m[letter] === undefined) m[letter] = idx; });
    return m;
  }, [filtered]);

  const scrollToLetter = (letter) => {
    const el = firstLetterRefs.current[letter];
    if (!el || !scrollRef.current) return;
    const container = scrollRef.current;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    container.scrollTop += elRect.top - containerRect.top - 12;
  };

  return (<>
    {/* Confirm remove */}
    {confirmId && (() => { const p = PEOPLE.find(x => x.id === confirmId); return p && <RemovePersonDialog person={p} onConfirm={() => { removePerson(confirmId); setConfirmId(null); setFullProfileId(null); }} onCancel={() => setConfirmId(null)} />; })()}

    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Sub-tab bar */}
      <PeopleSubTabBar subTab={subTab} setSubTab={setSubTab} isMobile={isMobile}/>

      {/* Page header */}
      <div style={{ padding: isMobile ? "10px 18px 0" : "14px 24px 0", background: T.panel, borderBottom: `1px solid ${T.edge}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: isMobile ? 22 : 26, fontFamily: "'Crimson Pro',serif", fontWeight: 300, color: T.ink, lineHeight: 1 }}>Your people</h2>
            <p style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{PEOPLE.length} relationships · all types</p>
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: "5px 9px", borderRadius: 12, border: `1px solid ${T.edge}`, background: T.mist, fontSize: 11, color: T.muted, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
            <option value="lastSeen">Recent</option>
            <option value="name">A–Z</option>
          </select>
          <button onClick={() => setAddPersonOpen(true)} style={{ padding: "7px 16px", borderRadius: 20, border: "none", cursor: "pointer", background: T.clay, color: "white", fontSize: 12, fontWeight: 600, boxShadow: `0 2px 8px ${T.clay}44`, flexShrink: 0 }}>+ Add</button>
        </div>

        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", paddingBottom: 10 }}>
          <button onClick={() => setTf("all")} style={{ padding: "5px 13px", borderRadius: 20, border: `1.5px solid ${tf === "all" ? T.ink : T.edge}`, cursor: "pointer", background: tf === "all" ? T.ink : T.mist, color: tf === "all" ? "white" : T.muted, fontSize: 11, fontWeight: tf === "all" ? 600 : 400, flexShrink: 0, transition: "all .14s" }}>
            All <span style={{ fontSize:11, background: tf === "all" ? "rgba(255,255,255,.2)" : T.edgeMid, borderRadius: 8, padding: "1px 5px", marginLeft: 3, color: tf === "all" ? "white" : T.muted }}>{counts.all}</span>
          </button>
          {Object.entries(REL).map(([k, cfg]) => counts[k] > 0 && (
            <button key={k} onClick={() => setTf(tf === k ? "all" : k)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${tf === k ? cfg.color : T.edge}`, cursor: "pointer", background: tf === k ? cfg.color + "18" : T.mist, color: tf === k ? cfg.color : T.muted, fontSize: 11, fontWeight: tf === k ? 600 : 400, flexShrink: 0, transition: "all .14s", boxShadow: tf === k ? `0 0 0 3px ${cfg.color}14` : "none" }}>
              <span style={{ fontSize: 12 }}>{cfg.icon}</span>{cfg.plural}
              <span style={{ fontSize:11, background: tf === k ? cfg.color : T.edgeMid, borderRadius: 8, padding: "1px 5px", color: tf === k ? "white" : T.muted, fontWeight: 600 }}>{counts[k]}</span>
            </button>
          ))}
          <div style={{ marginLeft: "auto", position: "relative" }}>
            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: T.edgeMid }}>⌕</span>
            <input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ padding: "6px 11px 6px 26px", borderRadius: 18, border: `1px solid ${T.edge}`, background: T.surface, fontSize: 11, color: T.ink, width: 160, fontFamily: "'DM Sans',sans-serif", outline: "none" }} />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>

        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 14px 12px 14px" : "16px 22px" }}>

          {/* ── People you may know ── */}
          {(() => {
            const visible = contactSuggestions.filter(s => !dismissedIds.has(s.id));
            if (!Capacitor.isNativePlatform() || (visible.length === 0 && !suggestionsLoading)) return null;
            return (
              <div style={{ marginBottom: 14, background: T.surface, border: `1px solid ${T.edge}`, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "11px 14px 8px", borderBottom: `1px solid ${T.edge}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14 }}>📱</span>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.bark, flex: 1 }}>People you may know</p>
                  <p style={{ fontSize: 11, color: T.muted }}>From your contacts · On Orbit</p>
                </div>
                {suggestionsLoading && (
                  <div style={{ padding: "14px 16px", fontSize: 12, color: T.muted, textAlign: "center" }}>Finding contacts on Orbit…</div>
                )}
                {!suggestionsLoading && visible.map((s, i) => {
                  const ini = (s.display_name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
                  return (
                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: i < visible.length - 1 ? `1px solid ${T.edge}` : "none" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", background: T.partner + "18", border: `1.5px solid ${T.partner}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: T.partner, fontFamily: "'Crimson Pro',serif", flexShrink: 0, overflow: "hidden" }}>
                        {s.photo ? <img src={s.photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" /> : ini}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.display_name}</p>
                        <p style={{ fontSize: 11, color: T.sage }}>✓ On Orbit</p>
                      </div>
                      <button
                        onClick={() => { setAddPersonPrefill(s); setAddPersonOpen(true); dismissSuggestion(s.id); }}
                        style={{ padding: "6px 14px", borderRadius: 20, background: T.clay, border: "none", color: "white", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}
                      >Add</button>
                      <button
                        onClick={() => dismissSuggestion(s.id)}
                        style={{ width: 26, height: 26, borderRadius: "50%", background: T.mist, border: `1px solid ${T.edge}`, cursor: "pointer", color: T.muted, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                      >✕</button>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {!isMobile && <div style={{ background: T.surface, border: `1px solid ${T.edge}`, borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 10px 10px", marginBottom: 10 }}>
            <p style={{ fontSize:11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>Your constellation</p>
            <PeopleOrbitalChart people={PEOPLE} selectedId={fullProfileId} onSelect={id => { setFullProfileId(id === fullProfileId ? null : id); }} onSelectMe={() => onNav("settings")} filterType={tf} />
            <p style={{ fontSize:11, color: T.muted, textAlign: "center", marginTop: 4 }}>Tap a person to open their profile</p>
          </div>}

          {filtered.length === 0 ? (
            <div style={{ padding: "48px 24px", textAlign: "center", background: T.surface, borderRadius: 16, border: `1px solid ${T.edge}`, maxWidth: 380 }}>
              <p style={{ fontSize: 28, marginBottom: 10 }}>{tf !== "all" ? REL[tf].icon : "👥"}</p>
              <h3 style={{ fontSize: 18, fontFamily: "'Crimson Pro',serif", fontWeight: 300, marginBottom: 6, color: T.ink }}>{search ? "No matches" : `No ${tf === "all" ? "people" : REL[tf].plural.toLowerCase()} yet`}</h3>
              <p style={{ fontSize: 12, color: T.muted, marginBottom: 20 }}>{search ? "Try a different search." : "Add someone to get started."}</p>
              {!search && <button onClick={() => setAddPersonOpen(true)} style={{ padding: "8px 20px", borderRadius: 20, background: T.clay, border: "none", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>+ Add person</button>}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10 }}>
              {filtered.map((p, idx) => {
                const letter = p.name[0].toUpperCase();
                const isFirstOfLetter = letterMap[letter] === idx;
                return (<div key={p.id} ref={isFirstOfLetter ? (el => { if (el) firstLetterRefs.current[letter] = el; }) : null}>
                  <DirCard p={p} isSelected={fullProfileId === p.id}
                    onClick={() => setFullProfileId(fullProfileId === p.id ? null : p.id)} />
                </div>);
              })}
            </div>
          )}
        </div>

        {/* iOS-style alphabet index */}
        {isMobile && (
          <div style={{ width: 22, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 8, userSelect: "none", overflowY: "hidden", zIndex: 20 }}>
            {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map(letter => {
              const has = !!letterMap[letter];
              return (<div key={letter} onTouchStart={() => has && scrollToLetter(letter)} onClick={() => has && scrollToLetter(letter)}
                style={{ fontSize:11, fontWeight: 700, lineHeight: 1.5, color: has ? T.partner : T.edgeMid, cursor: has ? "pointer" : "default", padding: "0 4px", textAlign: "center" }}>
                {letter}
              </div>);
            })}
          </div>
        )}

      </div>
    </div>

    {addPersonOpen && <AddPersonSheet prefill={addPersonPrefill} onClose={() => { setAddPersonOpen(false); setAddPersonPrefill(null); }} onAdd={(p) => { addPerson(p); setAddPersonOpen(false); setAddPersonPrefill(null); if (p.linkedUserId) setConnReqSentPopup(true); }} />}
    {connReqSentPopup && (
      <div onClick={() => setConnReqSentPopup(false)} style={{ position: "fixed", inset: 0, background: "rgba(30,16,8,0.45)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn .15s ease" }}>
        <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: 18, padding: "28px 28px 20px", maxWidth: 300, width: "88%", boxShadow: "0 8px 48px rgba(30,16,8,.22)", textAlign: "center", animation: "fadeUp .2s ease" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📨</div>
          <p style={{ fontSize: 16, fontFamily: "'Crimson Pro',serif", fontWeight: 600, color: T.ink, marginBottom: 6 }}>Connection request sent</p>
          <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.45, marginBottom: 20 }}>They'll see your request and can accept or decline.</p>
          <button onClick={() => setConnReqSentPopup(false)} style={{ width: "100%", padding: "11px", borderRadius: 11, background: T.clay, border: "none", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>OK</button>
        </div>
      </div>
    )}
    {fullProfileId && <PersonDrawer isMobile={isMobile} personId={fullProfileId} onClose={() => setFullProfileId(null)} onSchedule={() => { const id = fullProfileId; setFullProfileId(null); onNav("schedule", id); }} />}
  </>);
}
